import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Snackbar, Box } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, User, CreateUserRequest, UpdateUserRequest } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import UserForm from '../components/users/UserForm';
import ChangeEmailDialog from '../components/users/ChangeEmailDialog';
import ChangePasswordDialog from '../components/users/ChangePasswordDialog';
import ClientAssignmentManager from '../components/clientAssignments/ClientAssignmentManager';
import UserClientAssignmentDialog from '../components/clientAssignments/UserClientAssignmentDialog';
import twoFactorService from '../services/twoFactorService';
import SettingsBackButton from '../components/common/SettingsBackButton';

interface UsersPageState {
  page: number;
  rowsPerPage: number;
  search: string;
}

const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, hasPermission } = useAuth();
  const canManageUsers = isSuperAdmin || hasPermission('users');
  
  const [state, setState] = useState<UsersPageState>({
    page: 0,
    rowsPerPage: 10,
    search: '',
  });

  const [formOpen, setFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [changeEmailDialogOpen, setChangeEmailDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [userForEmailChange, setUserForEmailChange] = useState<User | null>(null);
  const [userForPasswordChange, setUserForPasswordChange] = useState<User | null>(null);
  const [clientAssignmentManagerOpen, setClientAssignmentManagerOpen] = useState(false);
  const [userClientAssignmentOpen, setUserClientAssignmentOpen] = useState(false);
  const [userForClientAssignment, setUserForClientAssignment] = useState<User | null>(null);
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [userFor2FA, setUserFor2FA] = useState<User | null>(null);
  const [twoFactorQrCode, setTwoFactorQrCode] = useState('');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorVerifyCode, setTwoFactorVerifyCode] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  // Fetch users
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', state.page, state.rowsPerPage, state.search],
    queryFn: () => userService.getUsers({
      page: state.page + 1,
      limit: state.rowsPerPage,
      search: state.search || undefined,
      includeInactive: true, // Show both active and inactive users
    }),
    enabled: canManageUsers,
    placeholderData: (previousData) => previousData,
  });

  const users = usersData?.data || [];
  const totalCount = usersData?.pagination?.total || 0;

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (userData: CreateUserRequest) => userService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setFormOpen(false);
      setSelectedUser(undefined);
      setSnackbar({
        open: true,
        message: 'User created successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create user',
        severity: 'error',
      });
    },
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: UpdateUserRequest }) => 
      userService.updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setFormOpen(false);
      setSelectedUser(undefined);
      setSnackbar({
        open: true,
        message: 'User updated successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update user',
        severity: 'error',
      });
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      
      // Show different messages based on deletion type
      let message = response.message || 'User deleted successfully!';
      let severity: 'success' | 'info' = 'success';
      
      if (response.type === 'soft_delete') {
        severity = 'info';
        message = response.message || 'User deactivated successfully (user had related records)';
      }
      
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    onError: (error: any) => {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete user',
        severity: 'error',
      });
    },
  });

  // Change email mutation
  const changeEmailMutation = useMutation({
    mutationFn: ({ id, newEmail }: { id: string; newEmail: string }) => 
      userService.changeUserEmail(id, newEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setChangeEmailDialogOpen(false);
      setUserForEmailChange(null);
      setSnackbar({
        open: true,
        message: 'Email changed successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to change email',
        severity: 'error',
      });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) => 
      userService.changeUserPassword(id, newPassword),
    onSuccess: () => {
      setChangePasswordDialogOpen(false);
      setUserForPasswordChange(null);
      setSnackbar({
        open: true,
        message: 'Password changed successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to change password',
        severity: 'error',
      });
    },
  });

  const handleAdd = () => {
    setSelectedUser(undefined);
    setFormOpen(true);
  };

  const handleEdit = async (user: User) => {
    try {
      const response = await userService.getUserById(user.id);
      setSelectedUser(response.data);
      setFormOpen(true);
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load user details',
        severity: 'error',
      });
    }
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleChangeEmail = (user: User) => {
    setUserForEmailChange(user);
    setChangeEmailDialogOpen(true);
  };

  const handleChangePassword = (user: User) => {
    setUserForPasswordChange(user);
    setChangePasswordDialogOpen(true);
  };

  const handleManageUserClients = (user: User) => {
    setUserForClientAssignment(user);
    setUserClientAssignmentOpen(true);
  };

  const handleManage2FA = async (user: User) => {
    setUserFor2FA(user);
    
    // If user has 2FA enabled, show disable option
    if (user.twoFactorEnabled) {
      setTwoFactorDialogOpen(true);
      return;
    }
    
    // Otherwise, setup 2FA for user
    setTwoFactorLoading(true);
    try {
      const response = await twoFactorService.adminEnableForUser(user.id);
      setTwoFactorQrCode(response.qrCode);
      setTwoFactorSecret(response.secret);
      setTwoFactorDialogOpen(true);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to setup 2FA',
        severity: 'error',
      });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!userFor2FA) return;
    
    setTwoFactorLoading(true);
    try {
      await twoFactorService.adminDisableForUser(userFor2FA.id);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setTwoFactorDialogOpen(false);
      setUserFor2FA(null);
      setSnackbar({
        open: true,
        message: '2FA has been disabled for this user',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to disable 2FA',
        severity: 'error',
      });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleClose2FADialog = () => {
    setTwoFactorDialogOpen(false);
    setUserFor2FA(null);
    setTwoFactorQrCode('');
    setTwoFactorSecret('');
    setTwoFactorVerifyCode('');
  };

  const handleVerify2FASetup = async () => {
    if (!userFor2FA || !twoFactorVerifyCode) return;
    
    setTwoFactorLoading(true);
    try {
      await twoFactorService.verifySetup(twoFactorVerifyCode);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleClose2FADialog();
      setSnackbar({
        open: true,
        message: '2FA has been enabled successfully!',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Invalid verification code',
        severity: 'error',
      });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedUser(undefined);
  };

  const handleSubmit = (userData: CreateUserRequest | UpdateUserRequest) => {
    if (selectedUser) {
      updateMutation.mutate({ id: selectedUser.id, userData: userData as UpdateUserRequest });
    } else {
      createMutation.mutate(userData as CreateUserRequest);
    }
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const handleSubmitEmailChange = (newEmail: string) => {
    if (userForEmailChange) {
      changeEmailMutation.mutate({ id: userForEmailChange.id, newEmail });
    }
  };

  const handleSubmitPasswordChange = (newPassword: string) => {
    if (userForPasswordChange) {
      changePasswordMutation.mutate({ id: userForPasswordChange.id, newPassword });
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      search: event.target.value,
      page: 0, // Reset to first page when searching
    }));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setState(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setState(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Show access denied if not super admin
  if (!canManageUsers) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert severity="error">
          Access denied. You do not have permission to manage users.
        </Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert severity="error">
          Error loading users: {(error as Error).message}
        </Alert>
      </div>
    );
  }

  // Helper function to get user initials
  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || 'U';
  };

  // Helper function to get avatar color class
  const getAvatarColorClass = (index: number) => {
    const colors = ['uav-blue', 'uav-green', 'uav-amber', 'uav-purple', 'uav-teal'];
    return colors[index % colors.length];
  };

  // Helper function to get role badge class
  const getRoleBadgeClass = (role: string) => {
    if (role === 'SUPER_ADMIN') return 'r-super';
    if (role === 'ADMIN') return 'r-admin';
    if (role === 'AGENCY') return 'r-admin';
    return 'r-publisher';
  };

  return (
    <div style={{ padding: '22px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{`
        :root {
          --surface: #FFFFFF;
          --surface-2: #F9FAFB;
          --surface-3: #FAFAFA;
          --border: #E5E7EB;
          --border-sub: #F3F4F6;
          --border-strong: #D1D5DB;
          --t1: #111827;
          --t2: #374151;
          --t3: #6B7280;
          --t4: #9CA3AF;
          --t5: #D1D5DB;
          --blue: #4F46E5;
          --blue-h: #4338CA;
          --blue-l: #EEF2FF;
          --blue-b: #C7D2FE;
          --green: #059669;
          --green-h: #047857;
          --green-l: #ECFDF5;
          --green-b: #A7F3D0;
          --amber: #D97706;
          --amber-l: #FFFBEB;
          --red: #DC2626;
          --red-h: #B91C1C;
          --red-l: #FEF2F2;
          --purple: #7C3AED;
          --purple-h: #6D28D9;
          --purple-l: #F5F3FF;
          --purple-b: #DDD6FE;
          --teal: #0D9488;
          --teal-l: #F0FDFA;
          --r: 6px;
          --rl: 8px;
          --sh-xs: 0 1px 2px rgba(0,0,0,.05);
          --sh-sm: 0 1px 3px rgba(0,0,0,.07), 0 1px 2px rgba(0,0,0,.04);
        }

        .users-page-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .ph {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .ph-title {
          font-size: 19px;
          font-weight: 700;
          letter-spacing: -0.4px;
          color: var(--t1);
          margin: 0;
        }

        .ph-sub {
          font-size: 13px;
          color: var(--t3);
          margin-top: 3px;
        }

        .toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .srch {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--r);
          padding: 7px 12px;
          width: 280px;
          box-shadow: var(--sh-xs);
          transition: all 0.15s;
        }

        .srch:focus-within {
          border-color: var(--blue);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
        }

        .srch svg {
          width: 14px;
          height: 14px;
          color: var(--t4);
          flex-shrink: 0;
        }

        .srch input {
          border: none;
          background: none;
          outline: none;
          font-family: inherit;
          font-size: 13px;
          color: var(--t1);
          width: 100%;
        }

        .srch input::placeholder {
          color: var(--t4);
        }

        .toolbar-right {
          margin-left: auto;
          display: flex;
          gap: 8px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 13px;
          border-radius: var(--r);
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.14s;
          white-space: nowrap;
          line-height: 1;
        }

        .btn svg {
          width: 13px;
          height: 13px;
          flex-shrink: 0;
        }

        .btn-primary {
          background: var(--blue);
          color: #fff;
          box-shadow: 0 1px 2px rgba(79, 70, 229, 0.25);
        }

        .btn-primary:hover {
          background: var(--blue-h);
        }

        .btn-secondary {
          background: var(--surface);
          color: var(--t2);
          border: 1px solid var(--border);
          box-shadow: var(--sh-xs);
        }

        .btn-secondary:hover {
          background: var(--surface-2);
          color: var(--t1);
        }

        .table-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--rl);
          box-shadow: var(--sh-xs);
          overflow: hidden;
          animation: fadeUp 0.22s ease forwards;
        }

        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .tscroll {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          min-width: 880px;
        }

        thead th {
          text-align: left;
          font-size: 10.5px;
          font-weight: 600;
          color: var(--t4);
          letter-spacing: 0.5px;
          text-transform: uppercase;
          padding: 10px 14px;
          border-bottom: 1px solid var(--border);
          background: var(--surface-3);
          white-space: nowrap;
        }

        thead th:first-child {
          padding-left: 18px;
        }

        thead th:last-child {
          text-align: right;
          padding-right: 18px;
        }

        tbody tr {
          transition: background 0.08s;
          border-bottom: 1px solid var(--border-sub);
        }

        tbody tr:last-child {
          border-bottom: none;
        }

        tbody tr:hover {
          background: #FAFBFF;
        }

        tbody td {
          padding: 12px 14px;
          font-size: 12.5px;
          vertical-align: middle;
          white-space: nowrap;
        }

        tbody td:first-child {
          padding-left: 18px;
        }

        tbody td:last-child {
          padding-right: 18px;
          text-align: right;
        }

        .c-id {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: var(--t4);
          font-weight: 500;
        }

        .user-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .uav {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11.5px;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
        }

        .uav-blue {
          background: linear-gradient(135deg, #4F46E5, #6D28D9);
        }

        .uav-green {
          background: linear-gradient(135deg, #059669, #0D9488);
        }

        .uav-amber {
          background: linear-gradient(135deg, #D97706, #EA580C);
        }

        .uav-purple {
          background: linear-gradient(135deg, #7C3AED, #DB2777);
        }

        .uav-teal {
          background: linear-gradient(135deg, #0D9488, #059669);
        }

        .c-name {
          font-size: 13px;
          font-weight: 500;
          color: var(--t1);
        }

        .c-email {
          font-size: 11.5px;
          color: var(--t3);
          margin-top: 1px;
        }

        .role-badge {
          display: inline-block;
          padding: 3px 9px;
          border-radius: 99px;
          font-size: 11.5px;
          font-weight: 600;
          white-space: nowrap;
        }

        .r-super {
          background: var(--purple-l);
          color: var(--purple);
          border: 1px solid var(--purple-b);
        }

        .r-admin {
          background: var(--blue-l);
          color: var(--blue);
          border: 1px solid var(--blue-b);
        }

        .r-publisher {
          background: #F3F4F6;
          color: #6B7280;
          border: 1px solid #E5E7EB;
        }

        .tfa {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 9px;
          border-radius: 99px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }

        .tfa svg {
          width: 11px;
          height: 11px;
          flex-shrink: 0;
        }

        .tfa-on {
          background: var(--green-l);
          color: var(--green);
          border: 1px solid var(--green-b);
        }

        .tfa-off {
          background: #F3F4F6;
          color: #6B7280;
          border: 1px solid #E5E7EB;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 9px;
          border-radius: 99px;
          font-size: 11.5px;
          font-weight: 600;
        }

        .status-badge::before {
          content: '';
          width: 5px;
          height: 5px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .s-active {
          background: var(--green-l);
          color: var(--green);
          border: 1px solid var(--green-b);
        }

        .s-active::before {
          background: var(--green);
        }

        .s-inactive {
          background: #F3F4F6;
          color: #6B7280;
          border: 1px solid #E5E7EB;
        }

        .s-inactive::before {
          background: #9CA3AF;
        }

        .act-row {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          justify-content: flex-end;
        }

        .actb {
          width: 28px;
          height: 28px;
          border-radius: 5px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          transition: all 0.12s;
          position: relative;
        }

        .actb svg {
          width: 13px;
          height: 13px;
          color: var(--t4);
        }

        .actb:hover svg {
          color: var(--t1);
        }

        .actb.edit:hover {
          background: var(--blue-l);
        }

        .actb.edit:hover svg {
          color: var(--blue);
        }

        .actb.mail:hover {
          background: var(--teal-l);
        }

        .actb.mail:hover svg {
          color: var(--teal);
        }

        .actb.pwd:hover {
          background: var(--amber-l);
        }

        .actb.pwd:hover svg {
          color: var(--amber);
        }

        .actb.tfa2:hover {
          background: var(--purple-l);
        }

        .actb.tfa2:hover svg {
          color: var(--purple);
        }

        .actb.assign:hover {
          background: var(--blue-l);
        }

        .actb.assign:hover svg {
          color: var(--blue);
        }

        .actb.del:hover {
          background: var(--red-l);
        }

        .actb.del:hover svg {
          color: var(--red);
        }

        .actb[data-tip]:hover::after {
          content: attr(data-tip);
          position: absolute;
          bottom: calc(100% + 6px);
          left: 50%;
          transform: translateX(-50%);
          background: var(--t1);
          color: #fff;
          font-size: 10.5px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 4px;
          white-space: nowrap;
          pointer-events: none;
          font-family: 'Inter', sans-serif;
          z-index: 1000;
        }

        .actb[data-tip]:hover::before {
          content: '';
          position: absolute;
          bottom: calc(100% + 2px);
          left: 50%;
          transform: translateX(-50%);
          border: 4px solid transparent;
          border-top-color: var(--t1);
          pointer-events: none;
          z-index: 1000;
        }

        .pag {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          padding: 9px 16px;
          border-top: 1px solid var(--border-sub);
          background: var(--surface-3);
        }

        .pag-info {
          font-size: 12px;
          color: var(--t4);
        }

        .pag-rpp {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--t2);
        }

        .pag-rpp select {
          border: 1px solid var(--border);
          background: var(--surface);
          border-radius: 5px;
          padding: 3px 6px;
          font-family: inherit;
          font-size: 12px;
          outline: none;
          cursor: pointer;
        }

        .pag-btns {
          display: flex;
          gap: 3px;
        }

        .pagb {
          width: 26px;
          height: 26px;
          border-radius: 5px;
          border: 1px solid var(--border);
          background: var(--surface);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.1s;
        }

        .pagb:hover:not(:disabled) {
          background: var(--surface-2);
        }

        .pagb:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .pagb svg {
          width: 12px;
          height: 12px;
          color: var(--t2);
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: var(--t4);
        }
      `}</style>

      <div className="users-page-container">
        <SettingsBackButton />
        {/* Page Header */}
        <div className="ph">
          <div>
            <h1 className="ph-title">User Management</h1>
            <p className="ph-sub">Manage admin users and their permissions</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <div className="srch">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search users…"
              value={state.search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="toolbar-right">
            <button className="btn btn-secondary" onClick={() => setClientAssignmentManagerOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Manage Client Assignments
            </button>
            <button className="btn btn-primary" onClick={handleAdd}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add User
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="table-card">
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>2FA</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">Loading users...</div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">No users found</div>
                    </td>
                  </tr>
                ) : (
                  users.map((user: User, index: number) => (
                    <tr key={user.id}>
                      <td>
                        <span className="c-id">{user.id.slice(0, 8)}...</span>
                      </td>
                      <td>
                        <div className="user-cell">
                          <div className={`uav ${getAvatarColorClass(index)}`}>
                            {getUserInitials(user)}
                          </div>
                          <div>
                            <div className="c-name">{userService.formatUserName(user)}</div>
                            <div className="c-email">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                          {userService.getRoleDisplayName(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`tfa ${user.twoFactorEnabled ? 'tfa-on' : 'tfa-off'}`}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.isActive ? 's-active' : 's-inactive'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="act-row">
                          <button className="actb edit" data-tip="Edit User" onClick={() => handleEdit(user)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button className="actb mail" data-tip="Change Email" onClick={() => handleChangeEmail(user)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                              <polyline points="22,6 12,13 2,6" />
                            </svg>
                          </button>
                          <button className="actb pwd" data-tip="Change Password" onClick={() => handleChangePassword(user)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          </button>
                          {user.role === 'ADMIN' && (
                            <button className="actb assign" data-tip="Assign Clients" onClick={() => handleManageUserClients(user)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="8.5" cy="7" r="4" />
                                <polyline points="17 11 19 13 23 9" />
                              </svg>
                            </button>
                          )}
                          <button className="actb tfa2" data-tip={user.twoFactorEnabled ? 'Manage 2FA' : 'Enable 2FA'} onClick={() => handleManage2FA(user)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                          </button>
                          <button className="actb del" data-tip="Delete User" onClick={() => handleDelete(user)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="pag">
              <span className="pag-info">
                {state.page * state.rowsPerPage + 1}-{Math.min((state.page + 1) * state.rowsPerPage, totalCount)} of {totalCount}
              </span>
              <div className="pag-rpp">
                <span>Rows per page:</span>
                <select value={state.rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="pag-btns">
                <button className="pagb" disabled={state.page === 0} onClick={(e) => handlePageChange(e, 0)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="11 17 6 12 11 7" />
                    <polyline points="18 17 13 12 18 7" />
                  </svg>
                </button>
                <button className="pagb" disabled={state.page === 0} onClick={(e) => handlePageChange(e, state.page - 1)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button className="pagb" disabled={(state.page + 1) * state.rowsPerPage >= totalCount} onClick={(e) => handlePageChange(e, state.page + 1)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <button className="pagb" disabled={(state.page + 1) * state.rowsPerPage >= totalCount} onClick={(e) => handlePageChange(e, Math.ceil(totalCount / state.rowsPerPage) - 1)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="13 17 18 12 13 7" />
                    <polyline points="6 17 11 12 6 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* User Form Dialog */}
      <UserForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        user={selectedUser}
        loading={createMutation.isPending || updateMutation.isPending}
        onOpenClientAssignments={() => {
          if (selectedUser) {
            setUserForClientAssignment(selectedUser);
            setUserClientAssignmentOpen(true);
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm User Removal</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: '8px' }}>
            Are you sure you want to remove user "{userToDelete ? userService.formatUserName(userToDelete) : ''}"?
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px' }}>
            <strong>Note:</strong> If this user has created any records (clients, projects, orders, etc.), 
            they will be deactivated instead of permanently deleted to maintain data integrity.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Email Dialog */}
      <ChangeEmailDialog
        open={changeEmailDialogOpen}
        onClose={() => {
          setChangeEmailDialogOpen(false);
          setUserForEmailChange(null);
        }}
        onSubmit={handleSubmitEmailChange}
        user={userForEmailChange || undefined}
        loading={changeEmailMutation.isPending}
      />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => {
          setChangePasswordDialogOpen(false);
          setUserForPasswordChange(null);
        }}
        onSubmit={handleSubmitPasswordChange}
        user={userForPasswordChange || undefined}
        loading={changePasswordMutation.isPending}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Client Assignment Manager */}
      <ClientAssignmentManager
        open={clientAssignmentManagerOpen}
        onClose={() => setClientAssignmentManagerOpen(false)}
      />

      {/* User-Specific Client Assignment Dialog */}
      <UserClientAssignmentDialog
        open={userClientAssignmentOpen}
        onClose={() => {
          setUserClientAssignmentOpen(false);
          setUserForClientAssignment(null);
        }}
        user={userForClientAssignment}
      />

      {/* 2FA Management Dialog */}
      <Dialog open={twoFactorDialogOpen} onClose={handleClose2FADialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {userFor2FA?.twoFactorEnabled ? 'Manage Two-Factor Authentication' : 'Setup Two-Factor Authentication'}
        </DialogTitle>
        <DialogContent>
          {userFor2FA?.twoFactorEnabled ? (
            <Box textAlign="center" py={2}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '64px', height: '64px', margin: '0 auto 16px' }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                2FA is enabled for {userFor2FA?.firstName} {userFor2FA?.lastName}
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
                You can disable two-factor authentication for this user. They will need to set it up again if re-enabled.
              </div>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Disabling 2FA will reduce the security of this account.
              </Alert>
            </Box>
          ) : (
            <Box textAlign="center" py={2}>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
                Scan this QR code with an authenticator app (Google Authenticator, Authy, etc.)
              </div>
              
              {twoFactorQrCode && (
                <Box 
                  component="img" 
                  src={twoFactorQrCode} 
                  alt="2FA QR Code"
                  sx={{ 
                    maxWidth: 200, 
                    mx: 'auto', 
                    display: 'block',
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    p: 1,
                    mb: 2
                  }}
                />
              )}

              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>
                Or enter this secret key manually:
              </div>
              <div style={{ 
                fontFamily: 'monospace', 
                backgroundColor: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '6px',
                wordBreak: 'break-all',
                marginBottom: '24px',
                fontSize: '14px'
              }}>
                {twoFactorSecret}
              </div>

              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>
                Enter the 6-digit code from your authenticator app to verify:
              </div>
              <TextField
                fullWidth
                label="Verification Code"
                value={twoFactorVerifyCode}
                onChange={(e) => setTwoFactorVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                }}
                placeholder="000000"
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose2FADialog}>Cancel</Button>
          {userFor2FA?.twoFactorEnabled ? (
            <Button 
              variant="contained" 
              color="error"
              onClick={handleDisable2FA}
              disabled={twoFactorLoading}
            >
              {twoFactorLoading ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleVerify2FASetup}
              disabled={twoFactorLoading || twoFactorVerifyCode.length !== 6}
            >
              {twoFactorLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersPage;
